import { useState } from 'react';
import { useNavigate } from 'react-router';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { FileSpreadsheet, FileUp, Laptop, PackagePlus, Plus, UserPlus, Zap, type LucideIcon } from 'lucide-react';
import type { Capability } from '@skhub/contracts';
import { useAuth } from '@/app/auth';
import { AssignAssetDialog } from '@/features/assets/AssignAssetDialog';
import { EmployeeFormDialog } from '@/features/employees/EmployeeDialogs';

type Action = { key: string; label: string; hint: string; icon: LucideIcon; capability: Capability; run: () => void };

/** The everyday IT Team tasks in one menu, from any page. Each entry follows the signed-in role's capabilities. */
export function QuickActions() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState<'assign' | 'employee' | null>(null);

  const all: { label: string; actions: Action[] }[] = [
    {
      label: 'Hardware',
      actions: [
        { key: 'assign', label: 'Assign an asset', hint: 'Give a person an available device', icon: PackagePlus, capability: 'inventory.write', run: () => setOpen('assign') },
        { key: 'new-asset', label: 'Add a new asset', hint: 'Register a device in the inventory', icon: Plus, capability: 'inventory.write', run: () => navigate('/assets/new') },
        { key: 'laptops', label: 'Available laptops', hint: 'In stock and ready to issue', icon: Laptop, capability: 'inventory.read', run: () => navigate('/assets?laptop=1&status=Available') },
      ],
    },
    {
      label: 'People',
      actions: [{ key: 'employee', label: 'Add an employee', hint: 'New joiner or contractor', icon: UserPlus, capability: 'people.write', run: () => setOpen('employee') }],
    },
    {
      label: 'Data',
      actions: [
        { key: 'import', label: 'Import a register', hint: 'Excel or CSV upload', icon: FileUp, capability: 'import.run', run: () => navigate('/import') },
        { key: 'reports', label: 'Export a report', hint: 'Download as Excel', icon: FileSpreadsheet, capability: 'reports.export', run: () => navigate('/reports') },
      ],
    },
  ];
  const groups = all
    .map((g) => ({ ...g, actions: g.actions.filter((a) => can(a.capability)) }))
    .filter((g) => g.actions.length > 0);

  if (groups.length === 0) return null;
  return (
    <>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger className="btn btn-secondary h-9 px-2.5 sm:px-3" aria-label="Quick actions">
          <Zap aria-hidden className="size-4 text-brand-ink" />
          <span className="hidden sm:inline">Quick actions</span>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content align="end" sideOffset={8} className="animate-pop z-50 w-72 rounded-[var(--radius-lg)] border border-line bg-surface p-1.5 shadow-[var(--shadow-pop)]">
            {groups.map((g, i) => (
              <DropdownMenu.Group key={g.label}>
                {i > 0 && <DropdownMenu.Separator className="my-1 h-px bg-line" />}
                <DropdownMenu.Label className="px-2.5 pt-1.5 pb-1 text-2xs font-medium text-fg-3">{g.label}</DropdownMenu.Label>
                {g.actions.map((a) => (
                  <DropdownMenu.Item
                    key={a.key}
                    onSelect={a.run}
                    className="group flex cursor-pointer items-center gap-3 rounded-[var(--radius-md)] px-2.5 py-2 outline-none data-[highlighted]:bg-surface-2"
                  >
                    <span className="grid size-8 shrink-0 place-items-center rounded-[var(--radius-md)] bg-surface-2 text-fg-2 transition-colors group-data-[highlighted]:bg-brand-soft group-data-[highlighted]:text-brand-ink">
                      <a.icon aria-hidden className="size-4" />
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate text-[13px] font-medium text-fg">{a.label}</span>
                      <span className="block truncate text-xs text-fg-3">{a.hint}</span>
                    </span>
                  </DropdownMenu.Item>
                ))}
              </DropdownMenu.Group>
            ))}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
      {open === 'assign' && <AssignAssetDialog onClose={() => setOpen(null)} />}
      {open === 'employee' && <EmployeeFormDialog onClose={() => setOpen(null)} onCreated={(id) => navigate(`/employees/${id}`)} />}
    </>
  );
}
